/**
 * Domain utilities for handling subdomain-based school ID detection
 * Supports both subdomain mode (hust.imleagues.com) and path parameter mode (/hust/)
 */
import StorageEngine from "./StorageEngine";
import appConfig from "../config/AppConfig";
class DomainUtils {
    // Cache for performance
    static _cache = {
        schoolId: null,
        isSubdomainMode: null,
        hostname: null
    };

    // System subdomains that should be ignored when extracting school ID
    static SYSTEM_SUBDOMAINS = ['www', 'api', 'cdn', 'static', 'admin', 'app', 'mobile', "admin", "account", "portal"];

    // Development domain patterns
    static DEV_DOMAINS = ['localhost', '127.0.0.1', 'imlocal.com'];

    /**
     * Get the current hostname
     * @returns {string} Current hostname
     */
    static getHostname() {
        if (typeof window === 'undefined') return '';
        return window.location.hostname;
    }

    /**
     * Check if current domain is a development domain
     * @returns {boolean} True if development domain
     */
    static isDevDomain() {
        const hostname = this.getHostname();
        return this.DEV_DOMAINS.some(devDomain => hostname.includes(devDomain));
    }

    /**
     * Extract school ID from subdomain
     * @returns {string|null} School ID from subdomain or null if not found
     */
    static getSubDomain() {
        const hostname = this.getHostname();

        // Use cache if hostname hasn't changed
        if (this._cache.hostname === hostname && this._cache.schoolId !== null) {
            return this._cache.schoolId;
        }

        let schoolId = null;

        try {
            // For development domains, check if subdomain exists
            if (this.isDevDomain()) {
                // Pattern: schoolId.imlocal.com or schoolId.localhost
                const parts = hostname.split('.');
                if (parts.length >= 2) {
                    const potential = parts[0];
                    // Check if it's not a system subdomain and not empty
                    if (potential &&
                        potential !== 'bit' &&  // bit.imlocal.com is the main domain in dev
                        !this.SYSTEM_SUBDOMAINS.includes(potential.toLowerCase())) {
                        schoolId = potential;
                    }
                }
            } else {
                // Production domains: schoolId.imleagues.com
                const parts = hostname.split('.');
                if (parts.length >= 3) { // e.g., ['hust', 'imleagues', 'com']
                    const potential = parts[0];
                    // Validate it's not a system subdomain
                    if (potential && !this.SYSTEM_SUBDOMAINS.includes(potential.toLowerCase())) {
                        schoolId = potential;
                    }
                }
            }
        } catch (error) {
            console.warn('Error parsing hostname for school ID:', error);
        }

        // Cache the result
        this._cache.hostname = hostname;
        this._cache.schoolId = schoolId;

        return schoolId;
    }

    /**
     * Check if current URL is using subdomain mode
     * @returns {boolean} True if subdomain mode is active
     */
    static isSubdomainMode() {
        const hostname = this.getHostname();

        // Use cache if hostname hasn't changed
        if (this._cache.hostname === hostname && this._cache.isSubdomainMode !== null) {
            return this._cache.isSubdomainMode;
        }

        const schoolId = this.getSubDomain();
        const isSubdomain = schoolId !== null;

        // Cache the result
        this._cache.isSubdomainMode = isSubdomain;

        return isSubdomain;
    }

    /**
     * Extract school ID from URL path (legacy format)
     * @param {string} pathname - URL pathname
     * @returns {string|null} School ID from path or null if not found
     */
    static getSchoolIdFromPath(pathname = '') {
        if (!pathname && typeof window !== 'undefined') {
            pathname = window.location.pathname;
        }

        try {
            // Remove leading slash and split
            const pathParts = pathname.replace(/^\//, '').split('/');

            // Check if first part after /rec/ could be a school ID
            if (pathParts.length >= 2 && pathParts[0] === 'rec' && pathParts[1]) {
                const potential = pathParts[1];
                // Basic validation - should be alphanumeric
                if (/^[a-zA-Z0-9\-_]+$/.test(potential)) {
                    return potential;
                }
            }
        } catch (error) {
            console.warn('Error parsing pathname for school ID:', error);
        }

        return null;
    }

    /**
     * Get current school ID with priority:
     * 1. From subdomain (if in subdomain mode) - match with networklist customDomain
     * 2. From customDomain (subdomain mode only)
     * 3. From shortId/currentSchoolId (path parameter mode only)
     * @param {Array} networkList - Optional network list for subdomain matching
     * @returns {string|null} Current school ID
     */
    static getCurrentSchoolId(networkList = null) {
        // Priority 1: Get from subdomain and match with networklist
        const subdomainId = this.getSubDomain();
        if (subdomainId && this.isSubdomainMode() && networkList) {
            // Find network where customDomain matches subdomain
            const matchingNetwork = networkList.find(network =>
                network.customDomain === subdomainId
            );
            if (matchingNetwork) {
                return matchingNetwork.id || matchingNetwork.networkId;
            }
        }

        // Fallback: direct subdomain ID if no network list provided
        if (subdomainId) {
            return subdomainId;
        }

        // Get stored object for fallback logic
        const storedObject = StorageEngine(appConfig.LAST_VISITED_NETWORK_ID_KEY).load();
        if (storedObject) {
            if (this.isSubdomainMode()) {
                // In subdomain mode, only consider customDomain as fallback
                if (storedObject.customDomain) {
                    return storedObject.customDomain;
                }
            } else {
                // In path parameter mode, use shortId or currentSchoolId
                const storedNetworkId = storedObject.shortId || storedObject.currentSchoolId;
                if (storedNetworkId && typeof storedNetworkId === "string" && storedNetworkId.length > 0) {
                    return storedNetworkId;
                }
            }
        }

        return null;
    }

    /**
     * Get school ID with network list matching for subdomain mode
     * This method should be used when network data is available
     * @param {Array} networkList - Array of network objects with customDomain property
     * @returns {string|null} Matched school ID from network list
     */
    static getSchoolIdWithNetworkMatching(networkList) {
        if (!networkList || !Array.isArray(networkList)) {
            return this.getCurrentSchoolId();
        }

        return this.getCurrentSchoolId(networkList);
    }

    /**
     * Build subdomain URL for given school ID and path
     * @param {string} schoolId - School identifier
     * @param {string} path - Target path
     * @returns {string} Complete subdomain URL
     */
    static buildSubdomainUrl(schoolId, path = '/') {
        if (!schoolId) return path;

        const hostname = this.getHostname();
        let baseHostname;

        if (this.isDevDomain()) {
            // Development: use bit.imlocal.com as base
            baseHostname = hostname.includes('imlocal.com') ? 'imlocal.com' : 'localhost:3039';
        } else {
            // Production: use imleagues.com
            baseHostname = 'imleagues.com';
        }

        const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
        const newHostname = `${schoolId}.${baseHostname}`;

        // Ensure path starts with /
        if (!path.startsWith('/')) {
            path = '/' + path;
        }

        return `${protocol}//${newHostname}${path}`;
    }

    /**
     * Build path parameter URL for given school ID and path (legacy format)
     * @param {string} schoolId - School identifier
     * @param {string} path - Target path (should start with /admin, /portal, etc.)
     * @returns {string} Complete path parameter URL
     */
    static buildPathParamUrl(schoolId, path = '/') {
        if (!schoolId) return path;

        // Remove leading slash from path
        const cleanPath = path.replace(/^\//, '');

        // Build the legacy format: /rec/schoolId/path
        return `/rec/${schoolId}/${cleanPath}`;
    }

    /**
     * Build URL based on current mode (subdomain vs path parameter)
     * @param {string} schoolId - School identifier
     * @param {string} path - Target path
     * @param {boolean} forceSubdomain - Force subdomain mode regardless of current mode
     * @returns {string} URL in appropriate format
     */
    static buildUrl(schoolId, path = '/', forceSubdomain = false) {
        if (!schoolId) return path;

        if (forceSubdomain || this.isSubdomainMode()) {
            return this.buildSubdomainUrl(schoolId, path);
        } else {
            return this.buildPathParamUrl(schoolId, path);
        }
    }

    /**
     * Redirect to subdomain URL if currently using path parameter format
     * @param {boolean} immediate - Whether to redirect immediately
     * @returns {boolean} True if redirect was initiated
     */
    static redirectToSubdomainIfNeeded(immediate = false) {
        if (typeof window === 'undefined') return false;

        const pathSchoolId = this.getSchoolIdFromPath();
        const subdomainSchoolId = this.getSubDomain();

        // If we have school ID in path but not in subdomain, redirect
        if (pathSchoolId && !subdomainSchoolId) {
            const currentPath = window.location.pathname;
            // Remove /rec/schoolId from path
            const newPath = currentPath.replace(new RegExp(`^/rec/${pathSchoolId}`), '/rec');

            const newUrl = this.buildSubdomainUrl(pathSchoolId, newPath + window.location.search + window.location.hash);

            if (immediate) {
                window.location.href = newUrl;
            } else {
                // Use replace to avoid back button issues
                window.location.replace(newUrl);
            }

            return true;
        }

        return false;
    }

    /**
     * Clear cache - useful for testing or when domain changes
     */
    static clearCache() {
        this._cache = {
            schoolId: null,
            isSubdomainMode: null,
            hostname: null
        };
    }

    /**
     * Get debug information about current domain parsing
     * @returns {Object} Debug information
     */
    static getDebugInfo() {
        return {
            hostname: this.getHostname(),
            isDevDomain: this.isDevDomain(),
            schoolIdFromDomain: this.getSubDomain(),
            schoolIdFromPath: this.getSchoolIdFromPath(),
            isSubdomainMode: this.isSubdomainMode(),
            currentSchoolId: this.getCurrentSchoolId(),
            cache: { ...this._cache }
        };
    }
}

export default DomainUtils;
